/**
 * ProviderFiltering Module
 * 
 * Handles provider-based filtering and bid provider management including:
 * - Provider filter application and clearing
 * - Marker filtering by provider
 * - Provider data extraction and processing
 * - Filter state management
 */

import Logger from '../../lib/logger';

export class ProviderFiltering {
  constructor(controller) {
    this.controller = controller;
  }

  // Filter markers by provider
  async filterMarkersByProvider(event) {
    // Prevent default button behavior and event bubbling
    event.preventDefault();
    event.stopPropagation();

    try {
      const button = event.currentTarget;
      const providerId = button.dataset.providerId;
      const providerName = button.dataset.providerName;

      if (!providerId) {
        Logger.error('No provider ID found');
        return;
      }

      Logger.debug(`Filtering markers for provider: ${providerName} (ID: ${providerId})`);

      // Store current filter using Stimulus values
      this.controller.providerFilterIdValue = parseInt(providerId);
      this.controller.providerFilterNameValue = providerName;

      // Clear cache when filter changes
      this.controller.layerMgmt.clearCache();

      // Show loading state
      this.controller.showSpinner();

      // Reload with filter - this now loads both addresses and providers
      await this.controller.reloadMarkersWithFilter(providerId);

      // IMPORTANT: Reset tracking variables so next zoom/move will fetch new data
      this.controller.previousZoom = this.controller.map.getZoom();
      this.controller.previousCenter = this.controller.map.getCenter();
      this.controller.previousBounds = this.controller.map.getBounds();
      Logger.debug('Reset map tracking variables after provider filter change');

      // Update UI to show filter status
      this.updateFilterStatus();

      // Switch to bid list tab
      Logger.debug('🔄 Switching to bid list tab...');
      this.switchToBidListTab();

      // Update bid providers and bid list tabs
      Logger.debug('🔄 Updating bid providers and bid list tabs...');
      await this.controller.loadBidProviders();
      await this.controller.loadBidList();

      // Trigger bids CRM with map filter
      Logger.debug('🔄 Triggering bids CRM with map filter...');
      //this.controller.layerMgmt.triggerBidsCrmWithMapFilter();

      Logger.debug('✅ Provider filter applied successfully');

    } catch (error) {
      Logger.error('Failed to filter markers by provider:', error);
      this.controller.showErrorMessage('Failed to filter markers');
    } finally {
      this.controller.hideSpinner();
    }
  }

  // Clear provider filter
  async clearProviderFilter(event) {
    // Prevent default button behavior and event bubbling
    event.preventDefault();
    event.stopPropagation();

    try {
      Logger.debug('Clearing provider filter');

      // Clear stored filter using Stimulus values
      this.controller.providerFilterIdValue = 0;
      this.controller.providerFilterNameValue = "";

      // Clear cache when filter is removed
      this.controller.layerMgmt.clearCache();

      // Show loading state
      this.controller.showSpinner();

      // Reload markers without filter - this now loads both addresses and providers
      await this.controller.reloadMarkersWithFilter(null);

      // IMPORTANT: Reset tracking variables so next zoom/move will fetch new data
      this.controller.previousZoom = this.controller.map.getZoom();
      this.controller.previousCenter = this.controller.map.getCenter();
      this.controller.previousBounds = this.controller.map.getBounds();
      Logger.debug('Reset map tracking variables after clearing provider filter');

      // Update UI
      this.updateFilterStatus();

      // Update bid providers and bid list tabs
      await this.controller.loadBidProviders();
      await this.controller.loadBidList();

      // Update bids CRM with map filter (without provider filter now)
      //this.controller.layerMgmt.triggerBidsCrmWithMapFilter();

    } catch (error) {
      Logger.error('Failed to clear provider filter:', error);
      this.controller.showErrorMessage('Failed to clear filter');
    } finally {
      this.controller.hideSpinner();
    }
  }

  // Get current provider filter via controller
  getCurrentProviderFilter() {
    return this.controller.getCurrentProviderFilter();
  }

  // Update filter status in UI
  updateFilterStatus() {
    const filterInfo = this.getCurrentProviderFilter();

    // Use Stimulus targets if available, fallback to getElementById
    const banner = this.controller.hasFilterBannerTarget
      ? this.controller.filterBannerTarget
      : document.getElementById('map-provider-filter-banner');
    const providerNameElement = this.controller.hasFilterProviderNameTarget
      ? this.controller.filterProviderNameTarget
      : document.getElementById('filter-provider-name');

    if (filterInfo && banner) {
      // Show banner and update provider name
      banner.classList.remove('d-none');
      if (providerNameElement) {
        providerNameElement.textContent = filterInfo.name;
      }
      Logger.debug(`✅ Filter banner shown for: ${filterInfo.name}`);
    } else if (banner) {
      // Hide banner
      banner.classList.add('d-none');
      Logger.debug('✅ Filter banner hidden');
    }
  }

  // Extract provider information from popup HTML
  extractProviderFromPopup(popupHtml, latLng) {
    try {
      // Create a temporary DOM element to parse the popup HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = popupHtml;

      // Extract address ID from the "View Bids" link
      const viewBidsLink = tempDiv.querySelector('a[href*="/bids_crm"]');
      if (!viewBidsLink) return null;

      const addressId = viewBidsLink.href.match(/address_id=(\d+)/)?.[1];
      if (!addressId) return null;

      // Extract bid count from the stats
      const bidStat = tempDiv.querySelector('.stat-value');
      const bidCount = bidStat ? parseInt(bidStat.textContent) || 0 : 0;

      if (bidCount === 0) return null;

      // Extract location name from popup header
      const locationElement = tempDiv.querySelector('.popup-title');
      const locationName = locationElement ? locationElement.textContent.trim() : 'Unknown Location';

      return {
        id: addressId,
        name: locationName,
        totalBids: bidCount,
        locations: [{
          id: addressId,
          name: locationName,
          bids: bidCount,
          lat: latLng.lat,
          lng: latLng.lng
        }]
      };

    } catch (error) {
      Logger.warn('Failed to extract provider from popup:', error);
      return null;
    }
  }

  // Switch to bid list tab
  switchToBidListTab() {
    try {
      Logger.debug('🔍 Looking for bid list tab button...');

      // Use Stimulus target if available, fallback to getElementById
      const bidListTabButton = this.controller.hasBidsTabTarget
        ? this.controller.bidsTabTarget
        : document.getElementById('bids-tab');

      if (!bidListTabButton) {
        Logger.warn('❌ Bid list tab button not found');
        Logger.debug('Available elements with "bids" in ID:',
          Array.from(document.querySelectorAll('[id*="bids"]')).map(el => el.id));
        return;
      }

      Logger.debug('✅ Found bid list tab button:', bidListTabButton);

      // Use Bootstrap's tab functionality if available, otherwise simulate click
      if (typeof bootstrap !== 'undefined' && bootstrap.Tab) {
        Logger.debug('📱 Using Bootstrap Tab API');
        const tabTrigger = new bootstrap.Tab(bidListTabButton);
        tabTrigger.show();
      } else {
        Logger.debug('🖱️ Using click simulation (Bootstrap not available)');
        // Fallback: simulate a click on the tab button
        bidListTabButton.click();
      }

      Logger.debug('✅ Switched to bid list tab');
    } catch (error) {
      Logger.error('❌ Failed to switch to bid list tab:', error);
      // Fallback: try clicking the button directly
      try {
        const bidListTabButton = this.controller.hasBidsTabTarget
          ? this.controller.bidsTabTarget
          : document.getElementById('bids-tab');
        if (bidListTabButton) {
          Logger.debug('🔄 Trying fallback click...');
          bidListTabButton.click();
        }
      } catch (fallbackError) {
        Logger.error('❌ Fallback tab switch also failed:', fallbackError);
      }
    }
  }

}
